function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { isUploadedImage } from '@prezly/uploads';
import { UPLOADCARE_CDN_URL, UPLOADCARE_FILE_DATA_KEY } from "../constants.mjs";
import { UploadcareGifVideo } from "./UploadcareGifVideo.mjs";
export var MAX_PREVIEW_SIZE = 3000;
export var DEFAULT_PREVIEW_SIZE = 2048;
export class UploadcareImage {
  static createFromUploadcareWidgetPayload(fileInfo, caption) {
    if (!fileInfo.originalImageInfo) {
      throw new Error('UploadcareImage was given a non-image FileInfo object');
    }

    return new UploadcareImage({
      effects: fileInfo.cdnUrlModifiers ? fileInfo.cdnUrlModifiers.split('-').slice(1) : [],
      filename: fileInfo.name,
      mimeType: fileInfo.mimeType,
      originalHeight: fileInfo.originalImageInfo.height,
      originalWidth: fileInfo.originalImageInfo.width,
      size: fileInfo.size,
      uuid: fileInfo.uuid,
      caption
    });
  }

  static createFromPrezlyStoragePayload(payload, caption) {
    return new UploadcareImage({
      effects: payload.effects || [],
      filename: payload.filename,
      mimeType: payload.mime_type,
      originalHeight: payload.original_height,
      originalWidth: payload.original_width,
      size: payload.size,
      uuid: payload.uuid,
      caption
    });
  }

  static isPrezlyStoragePayload(payload) {
    return isUploadedImage(payload);
  }

  constructor(_ref) {
    var _this = this;

    var {
      uuid,
      filename,
      mimeType,
      size,
      originalWidth,
      originalHeight,
      effects = [],
      caption
    } = _ref;

    _defineProperty(this, UPLOADCARE_FILE_DATA_KEY, void 0);

    _defineProperty(this, "uuid", void 0);

    _defineProperty(this, "filename", void 0);

    _defineProperty(this, "mimeType", void 0);

    _defineProperty(this, "size", void 0);

    _defineProperty(this, "originalHeight", void 0);

    _defineProperty(this, "originalWidth", void 0);

    _defineProperty(this, "effects", void 0);

    _defineProperty(this, "isGif", () => {
      return this.mimeType === 'image/gif';
    });

    _defineProperty(this, "format", function () {
      var imageFormat = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'auto';
      return _this.withEffect("/format/".concat(imageFormat, "/"));
    });

    _defineProperty(this, "preview", function () {
      var width = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      var height = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

      if (_this.isGif()) {
        // Do not resize GIF otherwise it breaks the animation
        return _this;
      }

      if (width === null && height === null) {
        return _this.withEffect('/preview/');
      }

      var effectiveWidth = Math.min(Math.round(width || MAX_PREVIEW_SIZE), MAX_PREVIEW_SIZE);
      var effectiveHeight = Math.min(Math.round(height || MAX_PREVIEW_SIZE), MAX_PREVIEW_SIZE);
      return _this.withEffect("/preview/".concat(effectiveWidth, "x").concat(effectiveHeight, "/"));
    });

    _defineProperty(this, "resize", function () {
      var width = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      var height = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

      if (width == null && height === null) {
        throw new Error('At least one function argument has to be non-null');
      }

      if (_this.isGif()) {
        // Do not resize GIF otherwise it breaks the animation
        return _this;
      }

      if (width === null) {
        return _this.withEffect("/resize/x".concat(height, "/"));
      }

      if (height === null) {
        return _this.withEffect("/resize/".concat(width, "/"));
      }

      return _this.withEffect("/resize/".concat(width, "x").concat(height, "/"));
    });

    _defineProperty(this, "crop", (width, height) => {
      return this.withEffect("/crop/".concat(width, "x").concat(height, "/"));
    });

    _defineProperty(this, "scaleCrop", function (width, height) {
      var center = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

      if (center) {
        return _this.withEffect("/scale_crop/".concat(width, "x").concat(height, "/center/"));
      }

      return _this.withEffect("/scale_crop/".concat(width, "x").concat(height, "/"));
    });

    _defineProperty(this, "smartCrop", function (width, height) {
      var type = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'smart';
      var alignment = arguments.length > 3 ? arguments[3] : undefined;

      if (alignment) {
        return _this.withEffect("/scale_crop/".concat(width, "x").concat(height, "/").concat(type, "/").concat(alignment, "/"));
      }

      return _this.withEffect("/scale_crop/".concat(width, "x").concat(height, "/").concat(type, "/"));
    });

    _defineProperty(this, "toGifVideo", () => {
      // The `gif2video` transformation is supported only for gifs,
      // otherwise the server responds with "400 Bad Request".
      if (!this.isGif()) {
        throw new Error("You can only convert a GIF to video. Given: \"".concat(this.mimeType, "\"."));
      }

      return new UploadcareGifVideo({
        uuid: this.uuid,
        filename: this.filename,
        mimeType: this.mimeType,
        size: this.size,
        width: this.originalWidth,
        height: this.originalHeight
      });
    });

    _defineProperty(this, "srcSet", width => {
      var doubleWidth = width * 2;
      var src1x = this.resize(width).cdnUrl;
      var src2x = this.resize(doubleWidth).cdnUrl;

      if (this.width < doubleWidth) {
        return "".concat(src1x, " ").concat(width, "w");
      }

      return "".concat(src1x, " ").concat(width, "w, ").concat(src2x, " ").concat(doubleWidth, "w");
    });

    _defineProperty(this, "toPrezlyStoragePayload", () => ({
      version: 2,
      uuid: this.uuid,
      filename: this.filename,
      mime_type: this.mimeType,
      size: this.size,
      original_width: this.originalWidth,
      original_height: this.originalHeight,
      effects: this.effects
    }));

    _defineProperty(this, "withEffect", effect => {
      return new UploadcareImage({
        uuid: this.uuid,
        filename: this.filename,
        mimeType: this.mimeType,
        size: this.size,
        originalWidth: this.originalWidth,
        originalHeight: this.originalHeight,
        effects: [...this.effects, effect]
      });
    });

    this.uuid = uuid;
    this.filename = filename;
    this.mimeType = mimeType;
    this.size = size;
    this.originalWidth = originalWidth;
    this.originalHeight = originalHeight;
    this.effects = effects;

    if (caption !== undefined) {
      this[UPLOADCARE_FILE_DATA_KEY] = {
        caption
      };
    }
  }

  get caption() {
    var _this$UPLOADCARE_FILE;

    return (_this$UPLOADCARE_FILE = this[UPLOADCARE_FILE_DATA_KEY]) === null || _this$UPLOADCARE_FILE === void 0 ? void 0 : _this$UPLOADCARE_FILE.caption;
  }

  get dimensions() {
    var [width, height] = dimensions([this.originalWidth, this.originalHeight], this.effects);
    return {
      width,
      height
    };
  }

  get width() {
    return this.dimensions.width;
  }

  get height() {
    return this.dimensions.height;
  }

  get aspectRatio() {
    var {
      width,
      height
    } = this.dimensions;
    return width / height;
  }

  get cdnUrl() {
    var cdnUrl = [UPLOADCARE_CDN_URL, this.uuid, // Prepend a dash only if effects exist.
    // It doesn't matter if there's a dash at the end of URL even if there are no effects,
    // but it looks cleaner without it.
    this.effects.length === 0 ? this.effects : ['', ...this.effects].join('-')].join('/');
    return "".concat(cdnUrl).concat(encodeURIComponent(this.filename));
  }

  get downloadUrl() {
    var downloadUrl = [UPLOADCARE_CDN_URL, this.uuid, ['', ...this.effects, '/inline/no/'].join('-')].join('/');
    return "".concat(downloadUrl).concat(encodeURIComponent(this.filename));
  }

}

function dimensions(_ref2, effects) {
  var [width, height] = _ref2;
  return effects.reduce(function (_ref3, effect) {
    var [w, h] = _ref3;
    var [_, name, params] = effect.split('/');

    switch (name) {
      case 'preview':
        {
          if (!params) {
            return fit([w, h], [DEFAULT_PREVIEW_SIZE, DEFAULT_PREVIEW_SIZE]);
          }

          var p = params.split('x');
          var px = parseInt(p[0], 10) || null;
          var py = parseInt(p[1], 10) || null;
          return fit([w, h], [px, py]);
        }

      case 'resize':
      case 'crop':
      case 'scale_crop':
        {
          var _p = params.split('x');

          var _px = parseInt(_p[0], 10) || null;

          var _py = parseInt(_p[1], 10) || null;

          return resize([w, h], [_px, _py]);
        }
    }

    return [w, h];
  }, [width, height]);
}

function fit(_ref4, _ref5) {
  var [width, height] = _ref4;
  var [maxWidth, maxHeight] = _ref5;
  var resizedWidth = width;
  var resizedHeight = height;

  if (maxWidth && maxWidth < resizedWidth) {
    resizedWidth = maxWidth;
    resizedHeight = Math.round(height * maxWidth / width);
  }

  if (maxHeight && maxHeight < resizedHeight) {
    resizedWidth = Math.round(width * maxHeight / height);
    resizedHeight = maxHeight;
  }

  return [resizedWidth, resizedHeight];
}

function resize(_ref6, _ref7) {
  var [width, height] = _ref6;
  var [maxWidth, maxHeight] = _ref7;

  if (maxWidth && maxHeight) {
    return [maxWidth, maxHeight];
  }

  if (maxWidth) {
    return [maxWidth, Math.round(height * maxWidth / width)];
  }

  if (maxHeight) {
    return [Math.round(width * maxHeight / height), maxHeight];
  }

  return [width, height];
}